import { Activity, Award, CheckCircle2 } from "lucide-react";

export function CertificateDocument({ candidate, formattedDate }) {
  return (
    <div
      data-testid="certificate-document"
      id="certificate"
      className="bg-white text-black rounded-2xl border-2 border-zinc-200 overflow-hidden print:rounded-none print:border-0 print:shadow-none"
    >
      <CertificateHeader candidateId={candidate.id} />
      <CertificateBody candidate={candidate} formattedDate={formattedDate} />
    </div>
  );
}

function CertificateHeader({ candidateId }) {
  return (
    <div className="bg-zinc-900 text-white py-6 px-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-yellow-500 flex items-center justify-center">
            <Activity className="w-6 h-6 text-black" />
          </div>
          <div>
            <div className="font-heading font-black text-lg tracking-tight">SecureRTO</div>
            <div className="text-xs text-zinc-400 font-mono">Sensor-Based Driving Test System</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono text-zinc-400">Certificate ID</div>
          <div className="text-sm font-mono font-bold text-yellow-500">{candidateId}</div>
        </div>
      </div>
    </div>
  );
}

function CertificateBody({ candidate, formattedDate }) {
  const scores = candidate.scores || {};
  const metrics = candidate.sensorData || {};
  const scoreItems = [
    { label: "Turns", score: scores.turns },
    { label: "Smoothness", score: scores.smoothness },
    { label: "Stability", score: scores.stability },
    { label: "Time", score: scores.time },
  ];

  const detailItems = [
    ["Vehicle Type", candidate.vehicleType],
    ["License Category", candidate.licenseType],
    ["RFID", candidate.rfid || "—"],
    ["Age", candidate.age != null ? `${candidate.age} years` : "—"],
    ["Test Duration", metrics.durationSec != null ? `${metrics.durationSec} s` : "—"],
    ["Turn Sequence", (metrics.detectedSequence || []).join(" → ") || "—"],
  ];

  return (
    <div className="p-8 sm:p-12">
      {/* Title */}
      <div className="text-center mb-10">
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center">
            <Award className="w-10 h-10 text-emerald-600" />
          </div>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-zinc-900 mb-2">
          Certificate of Completion
        </h1>
        <p className="text-zinc-500 text-sm">
          Two-Wheeler Driving Test - Sensor-Based Evaluation
        </p>
      </div>

      {/* Main Content */}
      <div className="text-center mb-10">
        <p className="text-zinc-600 mb-2">This is to certify that</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-black text-zinc-900 mb-2 border-b-2 border-yellow-500 inline-block pb-1 px-4">
          {candidate.name}
        </h2>
        <p className="text-zinc-600 mt-3">
          has successfully passed the sensor-based two-wheeler driving evaluation
          conducted at <strong>{candidate.testCenter}</strong> on{" "}
          <strong>{formattedDate}</strong>.
        </p>
      </div>

      {/* Score Summary */}
      <div className="bg-zinc-50 rounded-xl p-6 mb-8 border border-zinc-200">
        <div className="text-center mb-4">
          <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-500">
            Overall Score
          </span>
          <div className="text-5xl font-mono font-black text-emerald-600 mt-1">
            {candidate.overallScore}<span className="text-2xl text-zinc-400">/100</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          {scoreItems.map((item) => (
            <div key={item.label} className="text-center">
              <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">
                {item.label}
              </div>
              <div className="text-xl font-mono font-bold text-zinc-800">{item.score ?? "—"}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        {detailItems.map(([label, value]) => (
          <div key={label} className="flex justify-between items-center py-2 border-b border-zinc-100">
            <span className="text-xs font-mono text-zinc-500">{label}</span>
            <span className="text-sm font-medium text-zinc-800">{value}</span>
          </div>
        ))}
      </div>

      {/* Verification */}
      <div className="flex items-center justify-between pt-6 border-t border-zinc-200">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <span className="text-xs font-mono text-zinc-500">
            Verified by SecureRTO Sensor System
          </span>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono text-zinc-500">Result</div>
          <div className="text-lg font-heading font-black text-emerald-600">PASSED</div>
        </div>
      </div>
    </div>
  );
}
